import { getBacksplashLineItemCfn } from '../../kitchens/functions/get-backsplash-line-item.cfn';
import { Island } from '../../islands/island.class';
import { Kitchen } from '../../kitchens/kitchen.class';
import { RequiredSkuType } from '../enums';

export type MatchableItem = {
	sku: string;
	total: number;
	itemType: RequiredSkuType;
	category: string;
};

export const buildMatchableItemsCfn = (kitchen: Kitchen): MatchableItem[] => {
	const items: MatchableItem[] = [];
	const islands = kitchen.design.islands.children;

	islands.forEach((island: Island) => {
		// Island bases
		items.push({
			sku: island.sku,
			total: island.msrpBase,
			itemType: RequiredSkuType.ISLAND_BASE,
			category: 'island'
		});

		// Countertops
		const countertop = island.getCountertop();
		if (countertop) {
			const { sku, msrp, material: { isGranite } } = countertop;
			items.push({
				sku,
				total: msrp,
				itemType: isGranite ? RequiredSkuType.COUNTERTOP_GRANITE : RequiredSkuType.COUNTERTOP_CONCRETE,
				category: isGranite ? 'granite' : 'concrete'
			});
		}

		// Appliances/cutouts from island panel items
		const { items: islandItems } = island.msrpItemized;
		islandItems.forEach(({ sku, total, category }) => {
			items.push({
				sku,
				total,
				itemType: RequiredSkuType.APPLIANCE,
				category
			});
		});
	});

	// Accessories
	kitchen.accessories.children.forEach(({ sku, msrp }) => {
		items.push({
			sku,
			total: msrp,
			itemType: RequiredSkuType.ACCESSORY,
			category: 'accessory'
		});
	});

	// Backsplashes
	const backsplashLineItem = getBacksplashLineItemCfn(kitchen);
	if (backsplashLineItem) {
		items.push({
			sku: backsplashLineItem.sku,
			total: backsplashLineItem.total,
			itemType: RequiredSkuType.BACKSPLASH,
			category: 'backsplash'
		});
	}

	return items;
};

/**
 * Checks if a matchable item matches a required SKU using 3-tier matching:
 * 1. Item type constant match (e.g., 'COUNTERTOP_GRANITE' or 'COUNTERTOP' for both granite/concrete)
 * 2. SKU substring match (e.g., 'CCX4LE' matches item.sku containing that string)
 * 3. Category match (e.g., item.category === requiredSku)
 */
export const matchesRequiredSku = (item: MatchableItem, requiredSku: string): boolean => {
	// Tier 1: Item type constant match
	if (requiredSku === item.itemType) return true;

	// COUNTERTOP matches both granite and concrete
	if (requiredSku === RequiredSkuType.COUNTERTOP &&
		(item.itemType === RequiredSkuType.COUNTERTOP_GRANITE || item.itemType === RequiredSkuType.COUNTERTOP_CONCRETE)
	) return true;

	// Tier 2: SKU substring match
	if (item.sku.includes(requiredSku)) return true;

	// Tier 3: Category match
	if (item.category === requiredSku) return true;

	return false;
};
